import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import Dialog from "@/components/dialog/Dialog"
import BasicButton from "@/components/button/BasicButton"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import { colorTokens } from "@/components/colorTokens"
import type { ZoneType } from "./type"
import type { PlayerMetric, CustomPlayerMetric } from "@/types/metric"
import { parseCSV, matchPlayers, ImportPlayerMatch } from "../utils/importZones"
import { ZONE_CODE_BY_TYPE } from "./constants"
import { createGlobalStore } from "@/stores/global"

import "./exportImportDialog.scss"

type ImportDialogProps = {
  open: boolean
  onClose: () => void
  type: ZoneType
  playerMetrics: PlayerMetric[]
  setPlayerMetrics: React.Dispatch<React.SetStateAction<PlayerMetric[]>>
  onSave: (updatedMetrics: PlayerMetric[]) => void
}

const TYPE_LABELS: Record<ZoneType, string> = {
  speed: "Speed",
  acceleration: "Acceleration",
  deceleration: "Deceleration",
  hr: "Heart Rate",
}

type Step = 1 | 2 | 3

export default function ImportDialog({
  open,
  onClose,
  type,
  playerMetrics,
  setPlayerMetrics,
  onSave,
}: ImportDialogProps) {
  const { t } = useTranslation()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>(1)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState("")
  const [matches, setMatches] = useState<ImportPlayerMatch[]>([])
  const [isDragOver, setIsDragOver] = useState(false)

  const resetState = useCallback(() => {
    setStep(1)
    setFile(null)
    setError("")
    setMatches([])
    setIsDragOver(false)
  }, [])

  const handleClose = useCallback(() => {
    resetState()
    onClose()
  }, [onClose, resetState])

  const processFile = useCallback(
    (selectedFile: File) => {
      if (!selectedFile.name.endsWith(".csv")) {
        setError(t("button.zoneImportInvalidFile"))
        return
      }

      setFile(selectedFile)
      setError("")

      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target?.result as string
        const { rows } = parseCSV(content)

        if (rows.length === 0) {
          setError(t("button.zoneImportNoData"))
          return
        }

        // Filter rows for current metric type
        const filteredRows = rows.filter((r) => r.metricType === type)
        if (filteredRows.length === 0) {
          setError(t("button.zoneImportNoMetricType", { type: TYPE_LABELS[type] }))
          return
        }

        const playerMatches = matchPlayers(filteredRows, playerMetrics)
        setMatches(playerMatches)
        setStep(2)
      }
      reader.readAsText(selectedFile)
    },
    [type, playerMetrics, t],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) processFile(droppedFile)
    },
    [processFile],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (selectedFile) processFile(selectedFile)
    },
    [processFile],
  )

  const matchedCount = useMemo(
    () => matches.filter((m) => m.status === "matched").length,
    [matches],
  )
  const unmatchedCount = useMemo(
    () => matches.filter((m) => m.status === "unmatched").length,
    [matches],
  )

  const handleImport = useCallback(() => {
    const validMatches = matches.filter((m) => m.status === "matched" && m.matchedPlayer)

    const updated = [...playerMetrics]

    for (const match of validMatches) {
      const idx = updated.findIndex((p) => p.idfPlayer === match.matchedPlayer!.idfPlayer)
      if (idx === -1) continue

      const csvRow = match.csvRows[0]
      if (!csvRow) continue

      const zoneCode = ZONE_CODE_BY_TYPE[type]
      const customMetrics: CustomPlayerMetric[] = csvRow.zoneValues.map((value, i) => ({
        code: type === "hr" ? `${zoneCode}${i}` : `${zoneCode}${i + 1}`,
        value,
        type: "ZONE",
      }))

      updated[idx] = {
        ...updated[idx],
        max: csvRow.maxValue,
        active: csvRow.active,
        customMetricActive: csvRow.customMetricActive,
        customMetrics,
        isExpanded: csvRow.customMetricActive,
      }
    }

    setPlayerMetrics(updated)
    onSave(updated)

    setSnackBar({
      open: true,
      message: t("button.zoneImportSuccess", { count: validMatches.length }),
      severity: "success",
    })

    handleClose()
  }, [matches, playerMetrics, setPlayerMetrics, type, onSave, t, handleClose, setSnackBar])

  const renderStepDots = () => (
    <div className="ImportDialog__step-indicator">
      {[1, 2, 3].map((s) => (
        <span
          key={s}
          className={`ImportDialog__step-dot ${
            s === step
              ? "ImportDialog__step-dot--active"
              : s < step
                ? "ImportDialog__step-dot--done"
                : ""
          }`}
        />
      ))}
      <span>{t("button.zoneImportStep", { current: step, total: 3 })}</span>
    </div>
  )

  const renderStep1 = () => (
    <>
      <div
        className={`ImportDialog__dropzone ${isDragOver ? "ImportDialog__dropzone--active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}>
        <Icon type="upload" size={32} color={colors.neutral.grey[400]} />
        <p className="ImportDialog__dropzone-text">{t("button.zoneImportDropzone")}</p>
        <p className="ImportDialog__dropzone-hint">.csv</p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      {file && (
        <div className="ImportDialog__file-info">
          <Icon type="file-list" size={18} color={colors.neutral.grey[600]} />
          <span className="ImportDialog__file-name">{file.name}</span>
        </div>
      )}
      {error && <div className="ImportDialog__error">{error}</div>}
    </>
  )

  const renderStep2 = () => (
    <>
      <div className="ImportDialog__match-summary">
        <span>
          {t("button.zoneImportMatched")}:{" "}
          <span className="ImportDialog__match-count ImportDialog__match-count--matched">
            {matchedCount}
          </span>
        </span>
        <span>
          {t("button.zoneImportUnmatched")}:{" "}
          <span className="ImportDialog__match-count ImportDialog__match-count--unmatched">
            {unmatchedCount}
          </span>
        </span>
      </div>

      <div className="ImportDialog__match-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>{t("button.zoneImportFilePlayer")}</th>
              <th>#</th>
              <th>{t("button.zoneImportMatchedTo")}</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((match, idx) => (
              <tr key={idx}>
                <td>
                  <span
                    className={`ImportDialog__match-status ${
                      match.status === "matched"
                        ? match.matchType === "exact"
                          ? "ImportDialog__match-status--matched"
                          : "ImportDialog__match-status--partial"
                        : "ImportDialog__match-status--unmatched"
                    }`}>
                    {match.status === "matched" ? (
                      <Icon
                        type="check"
                        size={14}
                        color={colorTokens.interactive["text-success"]}
                      />
                    ) : (
                      <Icon type="close" size={14} color={colors.red[500]} />
                    )}
                  </span>
                </td>
                <td>{match.filePlayer.name}</td>
                <td>{match.filePlayer.uniformNumber}</td>
                <td>
                  {match.matchedPlayer ? (
                    `${match.matchedPlayer.name} (#${match.matchedPlayer.backNumber})`
                  ) : (
                    <span style={{ color: colors.neutral.grey[400] }}>
                      {t("button.zoneImportNotFound")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )

  const renderStep3 = () => (
    <>
      <div className="ImportDialog__summary-list">
        <div className="ImportDialog__summary-item">
          <span>{t("button.zoneMetricType")}</span>
          <span>{TYPE_LABELS[type]}</span>
        </div>
        <div className="ImportDialog__summary-item">
          <span>{t("button.zoneImportPlayersToUpdate")}</span>
          <span>{matchedCount}</span>
        </div>
        {unmatchedCount > 0 && (
          <div className="ImportDialog__summary-item">
            <span>{t("button.zoneImportPlayersSkipped")}</span>
            <span>{unmatchedCount}</span>
          </div>
        )}
      </div>

      <div className="ImportDialog__warning">
        <Icon type="information-fill" size={16} color={colors.yellow[600]} />
        <span>{t("button.zoneImportWarning", { count: matchedCount })}</span>
      </div>
    </>
  )

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return t("button.zoneImportUploadTitle")
      case 2:
        return t("button.zoneImportMatchTitle")
      case 3:
        return t("button.zoneImportConfirmTitle")
    }
  }, [step, t])

  return (
    <Dialog
      open={open}
      title={`${t("button.import")} - ${stepTitle}`}
      onClose={handleClose}
      footer={
        <div className="ImportDialog__footer">
          {step > 1 && (
            <div className="ImportDialog__footer-left">
              <BasicButton
                type="default-outlined"
                size="md"
                onClick={() => setStep((s) => (s - 1) as Step)}>
                {t("button.back")}
              </BasicButton>
            </div>
          )}
          <BasicButton type="default-outlined" size="md" onClick={handleClose}>
            {t("button.cancel")}
          </BasicButton>
          {step < 3 ? (
            <BasicButton
              type="primary-contained"
              size="md"
              onClick={() => setStep((s) => (s + 1) as Step)}
              disabled={step === 1 && matches.length === 0}>
              {t("button.next")}
            </BasicButton>
          ) : (
            <BasicButton
              type="primary-contained"
              size="md"
              onClick={handleImport}
              disabled={matchedCount === 0}>
              {t("button.import")}
            </BasicButton>
          )}
        </div>
      }>
      <div className="ImportDialog">
        {renderStepDots()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </Dialog>
  )
}
